import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { useSearchStore } from '../../store/searchStore';
import toast from 'react-hot-toast';
import { useAOS } from '../../hooks/useAnimations';
import {
  Target,
  ArrowRight,
  Calendar,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MessageSquare,
  Send,
  Filter,
  Search
} from 'lucide-react';
import type { Player } from '../../types/club';
import { playerSearchService } from '../../services/playerSearchService';
import PlayerCard from '../../components/Sporting/PlayerCard';
import PlayerListItem from '../../components/Sporting/PlayerListItem';
import ViewControls, { type ViewMode, type ItemsPerPage } from '../../components/Sporting/ViewControls';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import { FormattedAIAnalysis } from '../../utils/aiAnalysisFormatter';

interface Message {
  from: 'user' | 'agent';
  text: string;
  timestamp: Date;
  isSearchQuery?: boolean;
}

type SearchState = 'idle' | 'searching' | 'completed';

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  
  // Initialize AOS animations
  useAOS();
  
  const { user } = useAuthStore();
  const searchStore = useSearchStore();
  const location = useLocation();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<Message[]>([]);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [input, setInput] = useState('');
  const [newSearchInput, setNewSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const [aiAnalysis, setAiAnalysis] = useState<string>('');
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [results, setResults] = useState<Player[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<ItemsPerPage>(() => {
    const saved = localStorage.getItem('dashboardItemsPerPage');
    return (saved ? Number(saved) as ItemsPerPage : 24);
  });

  const [hasSearched, setHasSearched] = useState(false);

  // View controls state
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('dashboardViewMode');
    return (saved as ViewMode) || 'grid';
  });

  const suggestions = [
    t('dashboard.suggestion1'),
    t('dashboard.suggestion2'),
    t('dashboard.suggestion3'),
    t('dashboard.suggestion4')
  ];

  // Save view preferences to localStorage
  useEffect(() => {
    localStorage.setItem('dashboardViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('dashboardItemsPerPage', itemsPerPage.toString());
  }, [itemsPerPage]);

  // Restore search state from store on mount
  useEffect(() => {
    const savedSearch = searchStore.restoreSearch();
    if (savedSearch.hasSearched && savedSearch.results.length > 0) {
      setResults(savedSearch.results);
      setTotalResults(savedSearch.totalResults);
      setCurrentPage(savedSearch.currentPage);
      setFilters(savedSearch.filters);
      setHasSearched(savedSearch.hasSearched);
      setSearchState('completed');
      
      // Restore messages if available
      if (savedSearch.messages.length > 0) {
        setMessages(savedSearch.messages);
      }
    }
  }, []);

  useEffect(() => {
    if (location.state?.searchResults) {
      const { searchResults } = location.state;
      setResults(searchResults.jugadores);
      setTotalResults(searchResults.total);
      setFilters(searchResults.filtros_aplicados || {});
      setMessages([
        { from: 'agent', text: 'Aquí están los resultados anteriores. Puedes seguir filtrando.', timestamp: new Date() }
      ]);
      setHasSearched(true);
      setSearchState('completed');
    }
  }, [location.state]);

  const totalPages = Math.ceil(totalResults / itemsPerPage);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const generateAIAnalysis = async (players: Player[]): Promise<void> => {
    if (players.length === 0) return;

    setLoadingAnalysis(true);
    try {
      // Prepare player data for analysis - use correct field names
      const playersData = players.slice(0, 10).map(player => {
        const nombre = player.player_name || player.nombre_fbref || player.nombre_transfermarkt || 'N/A';
        const posicion = player.position || player.posicion_fbref || player.posicion_transfermarkt || 'N/A';
        const edad = player.edad_transfermarkt || (player.date_of_birth ? new Date().getFullYear() - new Date(player.date_of_birth).getFullYear() : null);
        const equipo = player.current_club_name || player.equipo_transfermarkt || player.equipo_fbref || 'N/A';
        const liga = player.competicion || player.nombre_liga || 'N/A';
        const valor_mercado = player.valor_mercado;
        const goles = player.goles || 0;
        const asistencias = player.asistencias || 0;
        const minutos = player.minutos_jugados || 0;
        const xg = player.xg_esperado || player.xg_por_90;
        const xag = player.xag_esperado || player.xag_por_90;
        const partidos = player.partidos_jugados || player.partidos || 0;

        return {
          nombre,
          posicion,
          edad,
          equipo,
          liga,
          valor_mercado,
          goles,
          asistencias,
          minutos,
          partidos,
          xg,
          xag,
          goles_por_90: player.goles_por_90,
          asistencias_por_90: player.asistencias_por_90,
          nacionalidad: player.nacion || player.citizenship || player.country_of_birth
        };
      });

      const prompt = `Eres un director técnico de scouting profesional. Analiza estos ${playersData.length} jugadores y genera un informe ejecutivo estructurado.

DATOS DE JUGADORES:
${JSON.stringify(playersData, null, 2)}

GENERA UN INFORME CON EXACTAMENTE ESTAS 4 SECCIONES (usa estos títulos):

1. ANÁLISIS TÉCNICO-TÁCTICO
Evalúa perfiles posicionales, características técnicas dominantes y complementariedad entre jugadores.

2. RELACIÓN VALOR/RENDIMIENTO
Analiza si los valores de mercado están justificados por las métricas de rendimiento (goles, asistencias, minutos, xG).

3. RECOMENDACIONES ESTRATÉGICAS
Prioriza fichajes según necesidades típicas de un club: edad, potencial, ROI, experiencia en competiciones.

4. INSIGHTS DE MÉTRICAS AVANZADAS
Interpreta datos de xG y xAG para identificar jugadores sobrerrendimiento o infrautilizados.

FORMATO:
- Máximo 250 palabras
- Tono ejecutivo y directo
- Usa números concretos
- Menciona nombres de jugadores específicos
- Cada sección debe tener 2-3 frases concisas

Respuesta en español.`;

      const response = await fetch('/api/ai-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt })
      });

      if (response.ok) {
        const data = await response.json();
        setAiAnalysis(data.analysis);
      } else {
        throw new Error('Error en el análisis IA');
      }
    } catch (error) {
      console.error('Error generating AI analysis:', error);
      setAiAnalysis('Error al generar el análisis. Por favor, intenta de nuevo.');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const sendMessage = async (text: string, page: number = 1) => {
    if (!text.trim()) {
      toast.error('Por favor, ingresa una consulta válida');
      return;
    }
    setLoading(true);
    setSearchState('searching');
    setMessages((msgs) => [...msgs, { from: 'user', text, timestamp: new Date(), isSearchQuery: true }]);
    setHasSearched(true);
    
    // Reset AI analysis state for new search
    setAiAnalysis('');
    setLoadingAnalysis(false);

    try {
      const offset = (page - 1) * itemsPerPage;
      const response = await playerSearchService.searchPlayersAI({
        query: text,
        limit: itemsPerPage,
        offset
      });

      const newFilters = response.filtros_aplicados || filters;
      setFilters(newFilters);

      const messageAgent =
        response.consulta_original ||
        `Encontré ${response.total} jugadores. ¿Quieres buscar de nuevo?`;
      const newMessages = [...messages, 
        { from: 'user', text, timestamp: new Date(), isSearchQuery: true } as Message,
        { from: 'agent', text: messageAgent, timestamp: new Date() } as Message
      ];
      setMessages(newMessages);

      setResults(response.jugadores);
      setTotalResults(response.total);
      setCurrentPage(page);
      
      // Save to search store
      searchStore.setSearchResults(response.jugadores, response.total, page);
      searchStore.setSearchContext(newMessages, newFilters, text);
      searchStore.setHasSearched(true);
      
      // Only mark as completed on successful search
      setSearchState('completed');
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.response?.data?.mensaje || 'Error en la búsqueda');
      setMessages((msgs) => [
        ...msgs,
        { from: 'agent', text: 'Error en la búsqueda. Intenta de nuevo.', timestamp: new Date() }
      ]);
      setResults([]);
      setTotalResults(0);
      
      // Reset to idle state on error so user can try again
      setSearchState('idle');
    } finally {
      setLoading(false);
      setInput('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input, 1);
  };

  const handleNewSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSearchInput.trim()) {
      toast.error('Por favor, ingresa una consulta válida');
      return;
    }
    
    // Reset search state for new search
    setSearchState('idle');
    setMessages([]);
    setResults([]);
    setTotalResults(0);
    setCurrentPage(1);
    setFilters({});
    setAiAnalysis('');
    setLoadingAnalysis(false);
    setHasSearched(false);
    
    // Set the new search input and trigger search
    setInput(newSearchInput);
    setNewSearchInput('');
    
    // Use timeout to ensure state updates are processed
    setTimeout(() => {
      sendMessage(newSearchInput, 1);
    }, 0);
  };


  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      const lastUserMsg = [...messages].reverse().find(m => m.from === 'user');
      if (lastUserMsg) sendMessage(lastUserMsg.text, page);
      const resultsSection = document.getElementById('dashboard-results-section');
      if (resultsSection) resultsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-center space-x-1.5 mt-6">
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1 || loading}
          className="p-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-white transition-all duration-200 bg-[#0F2A7D] hover:bg-[#0A1F5C]"
          title={t('dashboard.pagination.first')}
        >
          <ChevronsLeft className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
          className="p-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-white transition-all duration-200 bg-[#0F2A7D] hover:bg-[#0A1F5C]"
          title={t('dashboard.pagination.previous')}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        {getPageNumbers().map((page, i) => (
          <button
            key={i}
            onClick={() => (typeof page === 'number' ? handlePageChange(page) : undefined)}
            disabled={loading || typeof page === 'string'}
            className={`px-2.5 py-1.5 rounded-lg font-medium transition-all duration-200 text-sm ${
              page === currentPage
                ? 'text-white bg-[#0F2A7D]'
                : typeof page === 'string'
                ? 'text-gray-400 cursor-default'
                : 'bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 hover:text-[#0F2A7D] hover:border-[#0F2A7D]'
            }`}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
          className="p-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-white transition-all duration-200 bg-[#0F2A7D] hover:bg-[#0A1F5C]"
          title={t('dashboard.pagination.next')}
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages || loading}
          className="p-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-white transition-all duration-200 bg-[#0F2A7D] hover:bg-[#0A1F5C]"
          title={t('dashboard.pagination.last')}
        >
          <ChevronsRight className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  };

  const areas = [
    /*{
      id: 'training',
      title: t('dashboard.areas.training.title'),
      description: t('dashboard.areas.training.description'),
      icon: Dumbbell,
      href: '/training',
      gradient: 'from-green-600 via-emerald-700 to-teal-800',
      disabled: false
    },
    {
      id: 'teams',
      title: t('dashboard.areas.teams.title'),
      description: t('dashboard.areas.teams.description'),
      icon: Users,
      href: '/teams',
      gradient: 'from-purple-600 via-violet-700 to-indigo-800',
      disabled: false
    },*/
    {
      id: 'sporting',
      title: t('dashboard.areas.sporting.title'),
      description: t('dashboard.areas.sporting.description'),
      icon: Target,
      href: '/sporting',
      gradient: 'from-orange-600 via-amber-700 to-yellow-800',
      disabled: false
    }
   /* {
      id: 'medical',
      title: t('dashboard.areas.medical.title'),
      description: t('dashboard.areas.medical.description'),
      icon: Heart,
      href: '/medical',
      gradient: 'from-red-600 via-rose-700 to-pink-800',
      disabled: true
    },
    {
      id: 'personal',
      title: t('dashboard.areas.personal.title'),
      description: t('dashboard.areas.personal.description'),
      icon: User,
      href: '/profile',
      gradient: 'from-indigo-600 via-purple-700 to-violet-800',
      disabled: true
    }*/
  ];


  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header Bienvenida - Diseño Minimalista */}

      {/* Conversational Interface - Minimalist Design */}
      <div className="relative overflow-hidden bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Compact Header */}
        <div className="border-b border-gray-100 px-5 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="w-8 h-8 bg-[#0F2A7D] rounded-lg flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900"> {t('dashboard.hello')} ScoutMind AI</h3>
              </div>
            </div>
            {messages.length > 0 && (
              <button
                onClick={() => {
                  setMessages([]);
                  setResults([]);
                  setTotalResults(0);
                  setHasSearched(false);
                  setSearchState('idle');
                  setAiAnalysis('');
                  searchStore.clearSearch();
                }}
                className="text-xs text-gray-500 hover:text-gray-700 transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-50"
              >
                {t('dashboard.newConversation')}
              </button>
            )}
          </div>
        </div>
        
        {/* Chat Container - Compact with better scroll */}
        <div className="relative bg-gray-50/50">
          {/* Messages Container - Minimalist style */}
          <div className="max-h-[500px] min-h-[320px] overflow-y-auto">
            {/* Welcome Message - Cleaner */}
            {messages.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center h-[320px] px-5 text-center space-y-5">
                <div className="space-y-1.5">
                  <h4 className="text-xl font-bold text-gray-900">{t('dashboard.searchTitle')}</h4>
                  <p className="text-sm text-gray-600 max-w-md">
                    {t('dashboard.searchDescription')}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 max-w-2xl w-full mt-3">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(s)}
                      className="text-left p-3 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 hover:border-[#0F2A7D] transition-all duration-200 group"
                    >
                      <div className="flex items-start space-x-2.5">
                        <Target className="w-3.5 h-3.5 text-[#0F2A7D] mt-0.5 group-hover:scale-110 transition-transform" />
                        <span className="text-xs text-gray-700 group-hover:text-gray-900 leading-relaxed">{s}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages - Cleaner design */}
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`py-4 px-5 ${
                  msg.from === 'user' ? 'bg-white' : 'bg-gray-50/50'
                }`}
              >
                <div className="max-w-3xl mx-auto">
                  <div className="flex space-x-3">
                    {/* Compact Avatar */}
                    <div
                      className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${
                        msg.from === 'user'
                          ? 'bg-[#0F2A7D] text-white'
                          : 'bg-gradient-to-br from-purple-500 to-blue-600'
                      }`}
                    >
                      {msg.from === 'user' ? (
                        <span className="text-white text-xs font-semibold">
                          {user?.first_name?.charAt(0) || 'U'}
                        </span>
                      ) : (
                        <MessageSquare className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>
                    
                    {/* Message Content - Compact */}
                    <div className="flex-1 space-y-1.5">
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-xs text-gray-900">
                          {msg.from === 'user' ? (user?.first_name || 'Tú') : 'ScoutMind AI'}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {(msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <div className="prose prose-sm max-w-none">
                        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Loading State - Compact */}
            {loading && (
              <div className="py-4 px-5 bg-gray-50/50">
                <div className="max-w-3xl mx-auto">
                  <div className="flex space-x-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                      <MessageSquare className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="flex-1 space-y-1.5">
                      <span className="font-semibold text-xs text-gray-900">ScoutMind AI</span>
                      <div className="flex items-center space-x-2">
                        <div className="flex space-x-1">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                        <span className="text-xs text-gray-500">Analizando...</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area - Minimalist Compact Design */}
          <div className="border-t border-gray-100 bg-white p-4">
            <div className="max-w-3xl mx-auto">
              {searchState === 'idle' && (
                <div className="space-y-2">
                  <form onSubmit={handleSubmit} className="relative">
                    <div className="relative bg-gray-50 rounded-xl border border-gray-200 hover:border-[#0F2A7D] transition-all duration-300 focus-within:border-[#0F2A7D] focus-within:bg-white">
                      <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSubmit(e);
                          }
                        }}
                        placeholder={t('dashboard.searchPlaceholderInitial')}
                        disabled={loading}
                        rows={1}
                        className="w-full px-4 py-3 pr-12 text-sm text-gray-900 placeholder-gray-500 resize-none bg-transparent border-0 focus:outline-none focus:ring-0 disabled:bg-gray-50 disabled:text-gray-500"
                        style={{
                          minHeight: '48px',
                          maxHeight: '150px',
                        }}
                      />
                      <button
                        type="submit"
                        disabled={loading || !input.trim()}
                        className="absolute right-2 bottom-2 p-2 bg-[#0F2A7D] hover:bg-[#0A1F5C] disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-300 hover:scale-105 disabled:hover:scale-100"
                      >
                        {loading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Continue Conversation Area - Compact */}
              {searchState !== 'idle' && (
                <div className="space-y-2">
                  <form onSubmit={handleNewSearch} className="relative">
                    <div className="relative bg-gray-50 rounded-xl border border-gray-200 hover:border-[#0F2A7D] transition-all duration-300 focus-within:border-[#0F2A7D] focus-within:bg-white">
                      <textarea
                        value={newSearchInput}
                        onChange={(e) => setNewSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleNewSearch(e);
                          }
                        }}
                        placeholder={t('dashboard.continuePlaceholder')}
                        disabled={loading}
                        rows={1}
                        className="w-full px-4 py-3 pr-12 text-sm text-gray-900 placeholder-gray-500 resize-none bg-transparent border-0 focus:outline-none focus:ring-0 disabled:bg-gray-50 disabled:text-gray-500"
                        style={{
                          minHeight: '48px',
                          maxHeight: '150px',
                        }}
                      />
                      <button
                        type="submit"
                        disabled={loading || !newSearchInput.trim()}
                        className="absolute right-2 bottom-2 p-2 bg-[#0F2A7D] hover:bg-[#0A1F5C] disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-300 hover:scale-105 disabled:hover:scale-100"
                      >
                        {loading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
              
              {/* Quick Actions - Compact chips */}
              {searchState === 'completed' && results.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] font-medium text-gray-600 mb-2 px-1 uppercase tracking-wide">Acciones rápidas</p>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => generateAIAnalysis(results)}
                      disabled={loadingAnalysis}
                      className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 text-purple-700 rounded-lg text-xs font-medium transition-all duration-300 border border-purple-200 hover:border-purple-300 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <MessageSquare className="w-3 h-3" />
                      <span>{loadingAnalysis ? 'Analizando...' : 'Análisis IA'}</span>
                    </button>
                    <button
                      onClick={() => setNewSearchInput('Muéstrame más jugadores similares')}
                      className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 text-blue-700 rounded-lg text-xs font-medium transition-all duration-300 border border-blue-200 hover:border-blue-300"
                    >
                      <Search className="w-3 h-3" />
                      <span>Similares</span>
                    </button>
                    <button
                      onClick={() => setNewSearchInput('Filtra por jugadores más jóvenes')}
                      className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 text-green-700 rounded-lg text-xs font-medium transition-all duration-300 border border-green-200 hover:border-green-300"
                    >
                      <Filter className="w-3 h-3" />
                      <span>Más jóvenes</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Analysis Display - Compact and clean */}
      {aiAnalysis && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center space-x-2.5 mb-3">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-white" />
            </div>
            <div>
              <h4 className="text-base font-semibold text-gray-900">Análisis IA</h4>
              <p className="text-[10px] text-gray-500">{results.length} jugadores analizados</p>
            </div>
          </div>
          <FormattedAIAnalysis 
            analysisText={aiAnalysis}
            className="prose prose-sm max-w-none text-gray-700 text-sm"
          />
          <button
            onClick={() => results.length > 0 && generateAIAnalysis(results)}
            className="mt-3 text-xs text-purple-600 hover:text-purple-700 font-medium"
            disabled={loadingAnalysis}
          >
            Regenerar análisis
          </button>
        </div>
      )}

      {/* Results Section - Clean and compact */}
      {hasSearched && results.length > 0 && (
        <div id="dashboard-results-section" className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-5 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-gray-900">{t('dashboard.results.title')}</h4>
                  <div className="flex items-center space-x-1.5 mt-0.5">
                    <span className="text-lg font-bold text-blue-600">{totalResults}</span>
                    <span className="text-xs font-semibold text-gray-600">{t('dashboard.results.playersFound')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Player Cards Display with View Controls - Compact */}
          <div className="p-5 space-y-5">
            {/* View Controls */}
            <ViewControls
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(items) => {
                setItemsPerPage(items);
                setCurrentPage(1); // Reset to first page when changing items per page
              }}
              totalItems={results.length}
            />

            {/* Players Display - Grid or List */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {results.map((player, index) => (
                  <PlayerCard 
                    key={player.player_id || player.id_fbref || player.id_transfermarkt || `player-${index}`}
                    player={player}
                    onViewProfile={(id) => {
                      // Save current search state before navigating
                      searchStore.setSearchResults(results, totalResults, currentPage);
                      searchStore.setSearchContext(messages, filters, input);
                      searchStore.setHasSearched(true);
                      
                      // Navigate to player detail
                      navigate(`/sporting/player-detail/${id}`, {
                        state: {
                          from: '/dashboard',
                        }
                      });
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {results.map((player, index) => (
                  <PlayerListItem 
                    key={player.player_id || player.id_fbref || player.id_transfermarkt || `player-${index}`}
                    player={player}
                    onViewProfile={(id) => {
                      // Save current search state before navigating
                      searchStore.setSearchResults(results, totalResults, currentPage);
                      searchStore.setSearchContext(messages, filters, input);
                      searchStore.setHasSearched(true);
                      
                      // Navigate to player detail
                      navigate(`/sporting/player-detail/${id}`, {
                        state: {
                          from: '/dashboard',
                        }
                      });
                    }}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && renderPagination()}
          </div>
        </div>
      )}

      {/* No Results Message - Compact */}
      {!loading && hasSearched && results.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100 shadow-sm">
          <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('dashboard.results.noResults')}</h3>
          <p className="text-sm text-gray-600">{t('dashboard.results.noResultsDescription')}</p>
        </div>
      )}

      {/* Grid Áreas - Compact */}
      <div>
        <div className="text-center mb-8">
          <h2 className="text-2xl font-black text-text-primary tracking-tight">{t('dashboard.exploreAreas')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {areas.map((a) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.id}
                to={a.disabled ? '#' : a.href}
                className={`group relative overflow-hidden bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-[1.02] ${
                  a.disabled ? 'opacity-75' : 'hover:-translate-y-1'
                }`}
                onClick={(e) => a.disabled && e.preventDefault()}
              >
                <div className="relative z-10">
                  <div className="relative mb-4">
                    <div className="w-14 h-14 bg-[#0F2A7D] rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-300 group-hover:scale-105">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <h3 className="text-lg font-black text-text-primary mb-2 group-hover:text-[#0F2A7D] transition-colors duration-300">{a.title}</h3>
                  <p className="text-sm text-text-secondary mb-4 leading-relaxed font-medium">{a.description}</p>
                  {!a.disabled && (
                    <div className="flex items-center text-[#0F2A7D] group-hover:text-[#0A1F5C] transition-colors duration-300">
                      <span className="text-xs font-semibold mr-1.5">Explorar</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Actividad Reciente - Compact */}
      <div className="relative overflow-hidden bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2.5">
              <div className="p-2.5 bg-[#0F2A7D] rounded-lg">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-black text-text-primary tracking-tight">{t('dashboard.recentActivity')}</h3>
            </div>
          </div>
          <div className="text-center py-12">
            <TrendingUp className="w-14 h-14 text-text-tertiary mx-auto mb-3" />
            <p className="text-lg text-text-secondary font-bold">{t('dashboard.noActivity')}</p>
            <p className="text-sm text-text-tertiary mt-2 font-medium">{t('dashboard.noActivitySubtext')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
